import { LocationData } from '~/models/Destination'
import { ProfileDestination } from '~/models/ProfileDestination'
import { GlobeService } from '~/services/globe/service'
import { TranslationService } from '~/services/Translation'
import { ColorPalette } from '~/common/passporter-styles/themes/color'

export default class GlobeDestinationActions {
	static async initialize(
		globeId: string,
		centerAtCoordinates: LocationData,
		zoom: number
	): Promise<GlobeService> {
		const language = TranslationService.obtainUserLanguage()
		const map = await new GlobeService(globeId, language, centerAtCoordinates, zoom).initialize()
		map.addCountriesArea('wished-countries', ColorPalette.getColor('secondary', 500))
		map.addCountriesArea('visited-countries', ColorPalette.getColor('primary', 500))
		return map
	}

	static printDestinations(
		wishlist: ProfileDestination[],
		visited: ProfileDestination[],
		map: GlobeService
	): void {
		this.printWishedDestinations(wishlist, visited, map)
		this.printVisitedDestinations(visited, map)
	}

	static printWishedDestinations(
		wishlist: ProfileDestination[],
		visited: ProfileDestination[],
		map: GlobeService
	): void {
		const visitedIsoCodes = this.getDestinationIsoCodes(visited)
		const rawWishedIsoCodes = this.getDestinationIsoCodes(wishlist)
		const wishedIsoCodes = rawWishedIsoCodes.filter((el) => !visitedIsoCodes.includes(el))
		map.updateCountriesArea('wished-countries', wishedIsoCodes)
	}

	static printVisitedDestinations(visited: ProfileDestination[], map: GlobeService): void {
		const visitedIsoCodes = this.getDestinationIsoCodes(visited)
		map.updateCountriesArea('visited-countries', visitedIsoCodes)
	}

	private static getDestinationIsoCodes(destinations: ProfileDestination[]): string[] {
		const isoCodes: string[] = []
		destinations
			.filter((destination: ProfileDestination) => destination.countryIso)
			.forEach((destination: ProfileDestination) => {
				const iso = destination.countryIso!
				isoCodes.push(iso)
				if (this.REGIONS_BT_COUNTRY[iso]) isoCodes.push(...this.REGIONS_BT_COUNTRY[iso])
			})

		return [...new Set(isoCodes)].map((code) => code.toUpperCase())
	}

	static normalizeIso(iso: string): string {
		const lower = iso.toLowerCase()
		const countryIso = this.COUNTRIES_BY_TERRITORY[lower]
		if (countryIso) return countryIso
		return lower
	}

	private static REGIONS_BT_COUNTRY: Record<string, string[]> = {
		fr: [
			'tf', // Territorios Franceses Australes y Antárticos
			'gf', // Guayana Francesa
			'nc', // Nueva Caledonia
			'wf', // Wallis y Futuna
			'pf', // Polinesia Francesa
			'gp', // Guadalupe
			'mq', // Martinica
			'yt', // Mayotte
			'mf', // Saint-Martin
			'bl', // Saint-Barthélemy
			're', // Reunión
		],

		us: [
			'mp', // Islas Marianas del Norte
			'gu', // Guam
			'as', // Samoa Estadounidense
			'pr', // Puerto Rico
			'vi', // Islas Vírgenes de EE.UU.
		],

		nl: [
			'sx', // Sint Maarten
			'cw', // Curazao
			'aw', // Aruba
			'bq', // Caribe Neerlandés
		],

		au: [
			'hm', // Islas Heard y McDonald
		],

		no: [
			'sj', // Svalbard (ISO también incluye Jan Mayen)
		],

		gb: ['xs'], // unofficial, ISO 3166-2: GB-AK
	}

	private static COUNTRIES_BY_TERRITORY: Record<string, string> = {
		// France
		tf: 'fr', // Territorios Franceses Australes y Antárticos
		gf: 'fr', // Guayana Francesa
		nc: 'fr', // Nueva Caledonia
		wf: 'fr', // Wallis y Futuna
		pf: 'fr', // Polinesia Francesa
		gp: 'fr', // Guadalupe
		mq: 'fr', // Martinica
		yt: 'fr', // Mayotte
		mf: 'fr', // Saint-Martin
		bl: 'fr', // Saint-Barthélemy
		re: 'fr', // Reunión

		// United States
		mp: 'us', // Islas Marianas del Norte
		gu: 'us', // Guam
		as: 'us', // Samoa Estadounidense
		pr: 'us', // Puerto Rico
		vi: 'us', // Islas Vírgenes de EE.UU.

		// Netherlands
		sx: 'nl', // Sint Maarten
		cw: 'nl', // Curazao
		aw: 'nl', // Aruba
		bq: 'nl', // Caribe Neerlandés

		// Australia
		hm: 'au', // Islas Heard y McDonald

		// Norway
		sj: 'no', // Svalbard (and Jan Mayen)

		// United Kingdom
		xs: 'gb', // unofficial, ISO 3166-2: GB-AK
	}
}
